export const NOTIFICATION_TIME_GROUP_ORDER = [
	'Last hour',
	'Earlier today',
	'Yesterday',
	'Earlier this month',
	'Older',
];

const HOUR_MS = 60 * 60 * 1000;

const getZonedDateParts = (timestampMs, timeZone) => {
	const formatter = new Intl.DateTimeFormat('en-US', {
		timeZone: timeZone || 'UTC',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		hour12: false,
	});
	const values = Object.fromEntries(
		formatter.formatToParts(new Date(timestampMs)).map((part) => [part.type, part.value]),
	);

	return {
		year: Number(values.year),
		month: Number(values.month),
		day: Number(values.day),
		hour: values.hour === '24' ? 0 : Number(values.hour),
		minute: Number(values.minute),
		second: Number(values.second),
	};
};

const getTimeZoneOffsetMs = (utcMs, timeZone) => {
	const parts = getZonedDateParts(utcMs, timeZone);
	const asLocal = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
	return asLocal - utcMs;
};

const getStartOfZonedDayMs = (nowMs, timeZone) => {
	const parts = getZonedDateParts(nowMs, timeZone);
	const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, 0, 0, 0, 0);
	const offsetMs = getTimeZoneOffsetMs(asUtc, timeZone);
	return asUtc - offsetMs;
};

const getStartOfPreviousZonedDayMs = (nowMs, timeZone) => {
	const parts = getZonedDateParts(nowMs, timeZone);
	const previousDayNoonUtc = Date.UTC(parts.year, parts.month - 1, parts.day - 1, 12, 0, 0, 0);
	return getStartOfZonedDayMs(previousDayNoonUtc, timeZone);
};

const getZonedMonthKey = (timestampMs, timeZone) => {
	const parts = getZonedDateParts(timestampMs, timeZone);
	return `${parts.year}-${parts.month}`;
};

export const resolveTimeGroup = (notification, nowMs = Date.now(), timeZone = 'UTC') => {
	if (!notification?.timestamp) {
		return 'Older';
	}

	const timestampMs = Date.parse(notification.timestamp);
	if (Number.isNaN(timestampMs)) {
		return 'Older';
	}

	if (nowMs - timestampMs < HOUR_MS) {
		return 'Last hour';
	}

	const startOfTodayMs = getStartOfZonedDayMs(nowMs, timeZone);
	if (timestampMs >= startOfTodayMs) {
		return 'Earlier today';
	}

	const startOfYesterdayMs = getStartOfPreviousZonedDayMs(nowMs, timeZone);
	if (timestampMs >= startOfYesterdayMs) {
		return 'Yesterday';
	}

	if (getZonedMonthKey(timestampMs, timeZone) === getZonedMonthKey(nowMs, timeZone)) {
		return 'Earlier this month';
	}

	return 'Older';
};

export const groupNotificationsByTime = (notifications, timeZone = 'UTC', nowMs = Date.now()) => {
	const groups = new Map(NOTIFICATION_TIME_GROUP_ORDER.map((key) => [key, []]));

	for (const notification of notifications) {
		const key = resolveTimeGroup(notification, nowMs, timeZone);
		groups.get(key)?.push(notification);
	}

	return NOTIFICATION_TIME_GROUP_ORDER.filter((key) => (groups.get(key)?.length ?? 0) > 0).map((key) => ({
		key,
		items: groups.get(key) ?? [],
	}));
};
